import React from 'react';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';

import Page from 'app/shared/containers/Page';
import HeadWrapper from 'app/shared/components/HeadWrapper';

// Multibrand-UI Components
import Button from '@zg-rentals/multibrand-ui/lib/Button';
import Text from '@zg-rentals/multibrand-ui/lib/Text';

// rmw context
import { RMWContext } from 'app/shared/providers/RMWContext';

// Constants
import QueryConstants from 'app/shared/constants/QueryConstants';
import StringConstants from 'app/shared/constants/StringConstants';
import ErrorMessageConstants from 'app/shared/constants/ErrorMessageConstants';

// utils
import queryUtils from 'app/shared/utils/queryUtils';

// actions
import PostingActions from 'app/shared/actions/PostingActions';
import ListingTeardownActions from 'app/shared/actions/ListingTeardownActions';

// Lodash
import invert from 'lodash/invert';

// Selectors
import {
    UserSelectors,
    RoutingSelectors,
} from 'app/shared/selectors';

import pathsManifest from 'app/shared/pathsManifest';

import './style.scss';

const pageTitle = 'Deactivate your listing';
const deactivationMessage = 'This listing has been deactivated.';

const errorCodes = {
    INVALID: '400',
    UNAUTHORIZED: '403',
};

class ListingTeardownPage extends React.Component {
    static contextType = RMWContext;

    constructor(props) {
        super(props);

        this.state = {
            errorMessage: null,
            message: null,
            obfuscatedEmails: [],
            showDeactivateButton: false,
            encodedAlias: null,
            brand: null,
            redirectPath: '',
        };
    }


    componentWillMount() {
        const { location } = this.props;
        const paths = pathsManifest.getAllPaths();

        const queryParams = queryUtils.parse(location.search);
        const encodedAlias = queryParams[QueryConstants.ENCODED_ALIAS];
        const verificationToken = queryParams[QueryConstants.VERIFICATION_TOKEN];

        if (!encodedAlias && !verificationToken) {
            this.setState({
                redirectPath: paths.propertiesPage,
            });
        } else if (encodedAlias) {
            this.handleRequestTeardown(encodedAlias);
        } else {
            this.handleConfirmTeardown(verificationToken);
        }
    }

    handleConfirmTeardown(verificationToken) {
        const { brand } = this.context;
        const { user } = this.props;
        const paths = pathsManifest.getAllPaths();

        this.props
            .dispatch(ListingTeardownActions.confirmTeardown(verificationToken, brand))
            .then(() => {
                if (user.loggedIn) {
                    this.setState({
                        redirectPath: `${paths.propertiesPage}?${QueryConstants.LISTING_DEACTIVATED}=true`,
                    });
                } else {
                    this.setState({
                        message: deactivationMessage,
                        showDeactivateButton: false,
                    });
                }
            })
            .catch((err) => {
                this.setState({
                    errorMessage: err.message,
                    showDeactivateButton: false,
                });
            });
    }

    handleRequestTeardown(encodedAlias) {
        const { user } = this.props;
        const { brand } = this.context;
        if (user.loggedIn) {
            this.handleLoggedInUser(encodedAlias, brand);
        } else {
            this.enableDeactivateButton(encodedAlias, brand);
        }
    }

    enableDeactivateButton(encodedAlias, brand) {
        this.setState({
            brand,
            encodedAlias,
            showDeactivateButton: true,
        });
    }

    handleLoggedInUser(encodedAlias, brand) {
        this.props
            .dispatch(PostingActions.property.getDetailsForEncodedAlias(encodedAlias))
            .then((response) => {
                const { isContactEmailVerified, feedId, listingId } = response;
                const brandNames = invert(StringConstants.FORMATTED_BRAND_NAMES);
                const isNotFeedListing = brandNames[feedId] === brand;

                if (isNotFeedListing || isContactEmailVerified) {
                    // if listing is not feed OR contactEmail is verified, deacivate & redirect
                    this.handlePassthruDeactivation(listingId);
                } else {
                    this.enableDeactivateButton(encodedAlias, brand);
                }
            })
            .catch((err) => {
                if (err.code === errorCodes.UNAUTHORIZED) {
                    // probably logged in as wrong user, go through non-logged in scenario
                    this.enableDeactivateButton(encodedAlias, brand);
                } else {
                    this.setState({
                        errorMessage: ErrorMessageConstants.DEFAULT_TEARDOWN_ERROR,
                        showDeactivateButton: false,
                    });
                }
            });
    }

    handlePassthruDeactivation(listingId) {
        const { paths } = this.context;

        this.props
            .dispatch(PostingActions.property.deactivate(listingId))
            .then(() => {
                this.setState({
                    redirectPath: `${paths.propertiesPage}?${QueryConstants.LISTING_DEACTIVATED}=true`,
                });
            })
            .catch((err) => {
                this.setState({
                    errorMessage: err,
                    showDeactivateButton: false,
                });
            });
    }

    clickDeactivationButton(encodedAlias, brand) {
        this.props
            .dispatch(ListingTeardownActions.requestTeardown(encodedAlias, brand))
            .then((res) => {
                const { response } = res;
                const { message, obfuscatedEmails } = response;
                this.setState({
                    message,
                    obfuscatedEmails,
                    showDeactivateButton: false,
                });
            })
            .catch(() => {
                this.setState({
                    errorMessage: ErrorMessageConstants.DEFAULT_TEARDOWN_ERROR,
                    showDeactivateButton: false,
                });
            });
    }

    render() {
        const {
            errorMessage,
            message,
            obfuscatedEmails,
            showDeactivateButton,
            encodedAlias,
            brand,
            redirectPath,
        } = this.state;

        if (redirectPath) {
            return (
                <Redirect to={redirectPath} />
            );
        }
        return (
            <Page noPadding={true} className="ListingTeardownPage">
                <HeadWrapper
                    title={pageTitle}
                />
                <div className="ListingTeardownHero">
                    {message && (
                        <div className="ListingTeardownPage-message-text">
                            <Text type="body">{message}</Text>
                        </div>
                    )}
                    {(obfuscatedEmails && obfuscatedEmails.length > 0) && (
                        <div>
                            <div className="ListingTeardownPage-message-text">
                                <Text type="body">Deactivation requests sent to the following:</Text>
                            </div>
                            <div>
                                <ul>
                                    {
                                        obfuscatedEmails.map((email, i) => <ul key={i}>{email}</ul>)
                                    }
                                </ul>
                            </div>
                        </div>
                    )}
                    {errorMessage && (
                        <div className="ListingTeardownPage-error">
                            <Text type="body">{errorMessage}</Text>
                        </div>
                    )}
                    {showDeactivateButton && (
                        <div>
                            <div className="ListingTeardownPage-message-text">
                                <Text type="body">To deactivate listing, please click button below</Text>
                            </div>
                            <div>
                                <Button
                                    type="tertiary"
                                    onClick={() => this.clickDeactivationButton(encodedAlias, brand)}
                                >
                                    Request Listing Deactivation
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </Page>
        );
    }
}

const mapStateToProps = state => ({
    user: UserSelectors.makeUser(state),
    location: RoutingSelectors.makeLocation(state),
});

export { ListingTeardownPage };
export default connect(mapStateToProps)(ListingTeardownPage);
